import {Component, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CodaltComponent} from '../../codalt.component';
import {Checkpoint} from '../../classes/checkpoint';
import {CheckpointService} from '../../services/checkpoint.service';
import {Platform} from '../../classes/platform';
import {CheckpointResult} from '../../classes/checkpoint-result';
import {PlatformService} from '../../services/platform.service';
import {DatePipe} from '@angular/common';
import {CheckpointResultLevelStatusPipe} from '../../pipes/checkpoint-result-level-status.pipe';
import {CheckpointResultService} from '../../services/checkpoint-result.service';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {Routenames} from '../../classes/route-name.enum';
import {FormatResponseTextPipe} from '../../pipes/format-response-text.pipe';
import {CheckpointHeaderComponent} from '../../services/checkpoint-header/checkpoint-header.component';
import {BytesPipePipe} from '../../pipes/bytes.pipe';
import {CurlErrorCodesEnum} from '../../classes/curl-error-codes.enum';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs';

// for error references look at the following website : https://curl.se/libcurl/c/libcurl-errors.html
@Component({
    selector: 'app-checkpoint-result-overview',
    templateUrl: './checkpoint-result-overview.component.html',
    styleUrls: ['./checkpoint-result-overview.component.scss'],
    imports: [
        DatePipe,
        CheckpointResultLevelStatusPipe,
        BreadcrumbComponent,
        FormatResponseTextPipe,
        CheckpointHeaderComponent,
        BytesPipePipe,
    ]
})
export class CheckpointResultOverviewComponent extends CodaltComponent implements OnInit {
    readonly checkpointResult = signal<CheckpointResult>(new CheckpointResult());

    readonly platform = signal<Platform>(new Platform());
    readonly checkpoint = signal<Checkpoint>(new Checkpoint());

    readonly minRespTime: string = 'Minimal responseTime threshold breached';
    readonly maxRespTime: string = 'Maximum responseTime threshold breached';
    readonly minContSize: string = 'Minimal content size threshold breached';
    readonly maxContSize: string = 'Maximum content size threshold breached';
    readonly checksumMismatch: string = 'Checksum mismatch';

    readonly CurlErrorCodesEnum = CurlErrorCodesEnum;

    // Subscriptions of the current route params, replaced when navigating to another result
    private routeSubscriptions = new Subscription();

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointResultService: CheckpointResultService,
        private checkpointService: CheckpointService,
        private platformService: PlatformService,
        private titleService: Title,
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { platformId, checkId, checkResultId }) => {
                this.routeSubscriptions.unsubscribe();
                this.routeSubscriptions = new Subscription();
                this.subscriptions.add(this.routeSubscriptions);

                this.routeSubscriptions.add(
                    this.platformService.liveItem(+params.platformId, this.platformService.getPlatform(+params.platformId)).subscribe({
                        next: platform => {
                            this.platform.set(platform);
                            this.setCrumbs();
                            this.titleService.setTitle(`${this.platform().name} - Seire ${this.environment.titleAppend}`);
                        },
                        error: err => console.error(err)
                    })
                );
                this.routeSubscriptions.add(
                    this.checkpointService.liveItem(+params.checkId, this.checkpointService.getCheckpoint(+params.checkId)).subscribe({
                        next: checkpoint => {
                            this.checkpoint.set(checkpoint);
                            this.setCrumbs();
                        },
                        error: err => console.error(err)
                    })
                );

                let loadedContentHash: string = null;
                this.routeSubscriptions.add(
                    this.checkpointResultService.liveItem(+params.checkResultId, this.checkpointResultService.getCheckpointResult(+params.checkResultId)).subscribe({
                        next: (responseResult) => {
                            // Pushes don't contain the response content, so keep the one already loaded
                            this.checkpointResult.update(current => +current.id === +responseResult.id ? {...current, ...responseResult} : responseResult);
                            this.setCrumbs();

                            const hash = this.checkpointResult().hash;
                            if (hash && hash !== loadedContentHash) {
                                loadedContentHash = hash;
                                this.routeSubscriptions.add(
                                    this.checkpointResultService.getCheckpointResultContent(hash).subscribe({
                                        next: (checkpointResultContent) => {
                                            this.checkpointResult.update(checkpointResult => ({...checkpointResult, checkpoint_results_content: checkpointResultContent}));
                                            this.setCrumbs();
                                        },
                                        error: err => console.error(err)
                                    })
                                );
                            }
                        },
                        error: err => console.error(err)
                    })
                );
            })
        );
    }

    setCrumbs() {
        this.crumbs = [
            {name: 'Seire', route: Routenames.seire},
            {name: `Platform : ${this.platform().name}`, route: `${Routenames.seire}/${this.platform().id}`},
            {name: `Checkpoint : ${this.checkpoint().name}`, route: `${Routenames.seire}/${this.platform().id}/overview/${this.checkpoint().id}`},
            {
                name: `Checkpoint resultaat : ${this.checkpointResult().id}`,
                route: `${Routenames.seire}/${this.platform().id}/overview/${this.checkpoint().id}/${this.checkpointResult().id}`
            }
        ];
    }
}
